import { z } from "zod";
import { apiClient } from "./api";
import {
  type ResponseAssetProjection,
  chatSseEventSchema,
  responseAssetProjectionSchema,
  stepCallWireSchema,
} from "./chat-event-schemas";
import { MessageTooLongError, parseMessageTooLongDetail } from "./chat-message-limit";
import { formatDate } from "./format";
import { LlmCostLimitError, parseLlmCostLimitScope } from "./llm-cost-limits";
import { type ReportPointer, reportPointerSchema, reportRefSchema } from "./reports";
import { sourceKindSchema } from "./sources";
import {
  type Operation,
  type PreparationState,
  storyPreparationRefSchema,
} from "./story-preparation-ref";
import { supabase } from "./supabase";

// --------------------------------------------------------------------------- //
// Attachment upload / delete (`add-chat-attachments`)                          //
// --------------------------------------------------------------------------- //

export const attachmentKindSchema = z.enum(["image", "document"]);
export type AttachmentKind = z.infer<typeof attachmentKindSchema>;

export const attachmentStatusSchema = z.enum(["pending", "ready", "failed"]);
export type AttachmentStatus = z.infer<typeof attachmentStatusSchema>;

export const attachmentUploadResponseSchema = z.object({
  attachment_id: z.guid(),
  filename: z.string(),
  kind: attachmentKindSchema,
  status: attachmentStatusSchema,
  extracted_preview: z.string().nullable().optional(),
  error: z.string().nullable().optional(),
});
export type AttachmentUploadResponse = z.infer<typeof attachmentUploadResponseSchema>;

// Declared here — before any function that uses it — to avoid TDZ fragility
// (declaration-before-use). Both `uploadAttachment` and `fetchAttachmentBlob`
// reference this constant; keeping it at module scope means it is initialized
// once at module evaluation time, not re-computed per call.
const baseUrl = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");

/** Upload a single file to `POST /chat/attachments`.
 * Returns the backend-assigned id, kind, status, and any error string.
 * Uses raw fetch (not `apiClient`) because the body is multipart FormData. */
export async function uploadAttachment(
  file: File,
  sessionId: string | undefined,
): Promise<AttachmentUploadResponse> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const form = new FormData();
  form.append("file", file);
  if (sessionId !== undefined) form.append("session_id", sessionId);

  const response = await fetch(`${baseUrl}/chat/attachments`, {
    method: "POST",
    headers,
    body: form,
  });
  if (!response.ok) {
    const text = await response.text();
    let payload: unknown = text;
    try {
      payload = JSON.parse(text);
    } catch {
      // keep raw text
    }
    const detail =
      payload &&
      typeof payload === "object" &&
      "detail" in payload &&
      typeof (payload as { detail: unknown }).detail === "object" &&
      (payload as { detail: { code?: unknown } }).detail !== null &&
      typeof (payload as { detail: { code?: unknown } }).detail.code === "string"
        ? (payload as { detail: { code: string } }).detail.code
        : typeof payload === "string"
          ? payload
          : `upload failed with status ${response.status}`;
    throw new Error(detail);
  }
  const json: unknown = await response.json();
  return attachmentUploadResponseSchema.parse(json);
}

/** Fetch the raw bytes for an attachment via `GET /chat/attachments/{id}/content`.
 * Requires auth (Bearer token) — returns the response as a Blob so the
 * caller can create an object URL for rendering or trigger a download.
 * Throws if the response is not ok. */
export async function fetchAttachmentBlob(attachmentId: string): Promise<Blob> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const url = `${baseUrl}/chat/attachments/${attachmentId}/content`;
  const response = await fetch(url, { headers });
  if (!response.ok) {
    throw new Error(`attachment fetch failed: ${response.status}`);
  }
  return response.blob();
}

export type ResponseAssetByteKind = "rendition" | "original";

/** Fetch the raw bytes for a knowledge-asset relation via
 * `GET /chat/sessions/{sessionId}/messages/{messageId}/assets/{relationId}/{kind}`.
 * Requires auth (Bearer token) — returns the response as a Blob so the
 * caller can create an object URL for rendering or trigger a download.
 * `relationId` is the `chat_message_assets` row id from
 * `ResponseAssetProjection.relation_id`, never a global asset id — it is
 * meaningless outside the owning session, which is exactly what the
 * authorized route enforces (a mismatched session/message/relation triple
 * resolves as a neutral 404, never a 403). Throws if the response is not ok. */
export async function fetchResponseAssetBlob(args: {
  sessionId: string;
  messageId: string;
  relationId: string;
  kind: ResponseAssetByteKind;
}): Promise<Blob> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const url = `${baseUrl}/chat/sessions/${encodeURIComponent(args.sessionId)}/messages/${encodeURIComponent(args.messageId)}/assets/${encodeURIComponent(args.relationId)}/${args.kind}`;
  const response = await fetch(url, { headers });
  if (!response.ok) {
    throw new Error(`response asset fetch failed: ${response.status}`);
  }
  return response.blob();
}

/** Delete an unsent attachment via `DELETE /chat/attachments/{id}`. */
export async function deleteAttachment(attachmentId: string): Promise<void> {
  await apiClient.delete(
    `/chat/attachments/${attachmentId}`,
    z.unknown().transform(() => undefined),
  );
}

export type ReasoningLevel = "off" | "low" | "medium" | "high";
export type ReasoningPreference = "auto" | ReasoningLevel;

export const REASONING_PREFERENCES: readonly ReasoningPreference[] = [
  "auto",
  "off",
  "low",
  "medium",
  "high",
];

export type ChatStreamEvent =
  | {
      type: "session";
      session_id: string;
      /** Persisted chat_messages ids. Optional while old/new deployments overlap. */
      user_message_id?: string;
      assistant_message_id?: string;
    }
  | {
      type: "retrieval_summary";
      vector_hits: number;
      bm25_hits: number;
      graph_hits: number;
      sparse_hits: number;
      fused_count: number;
    }
  | { type: "reasoning_level"; level: ReasoningLevel }
  | { type: "token"; content: string }
  | { type: "thinking"; content: string }
  | {
      type: "usage";
      prompt_tokens: number;
      completion_tokens: number;
      total_tokens: number;
      cost_usd?: number;
      /** Per-turn input tokens served from the provider's prompt cache.
       * `cache_read_input_tokens` is the meaningful figure; the creation
       * counterpart is ~0 outside Anthropic. */
      cache_read_input_tokens?: number;
      cache_creation_input_tokens?: number;
    }
  | {
      type: "citation";
      id: string;
      title: string;
      preview: string;
      user_metadata?: string | null;
      source?: string;
      source_ref?: string;
      external_url?: string;
    }
  | { type: "learned_fact"; knowledge_item_id: string; auto_approved?: boolean }
  | {
      type: "fact_proposal";
      draft_id: string;
      assistant_message_id: string;
      text: string;
      confidence: number;
    }
  | {
      type: "user_memory_draft";
      draft_id: string;
      assistant_message_id: string;
      operation: UserMemoryDraftOperation;
      text?: string;
      target_id?: string;
      target_text?: string;
    }
  | {
      type: "stage_access_request";
      stage: "demo" | "dev";
      status: "pending";
    }
  | {
      type: "tool_use";
      id: string;
      name: string;
      input: Record<string, unknown>;
    }
  | {
      type: "tool_result";
      tool_use_id: string;
      ok: boolean;
      content_preview: string;
      bytes: number;
      error?: string;
      ref?: string;
      stage?: string;
      ref_fallback?: boolean;
      tried_refs?: string[];
      /** Outcome of a knowledge lifecycle tool (cancel/refine) so the
       * client can update the affected cards without a reload. */
      knowledge_lifecycle?: Record<string, unknown>;
    }
  | {
      type: "step";
      id: string;
      status: "start" | "done" | "error";
      elapsed_ms?: number;
      detail?: {
        hits?: number;
        bytes?: number;
        stage?: string;
        fused?: number;
        error?: string;
        decision?: string;
        reason?: string;
        tier?: string;
        selected?: {
          provider: string;
          model_id: string;
          display_name?: string;
        };
        shadow?: boolean;
        sticky?: boolean;
        would_skip?: boolean;
        skipped?: boolean;
      };
      usage?: StepUsageWire;
    }
  | { type: "citation_warning"; fabricated_markers: string[] }
  | { type: "source_redaction_warning"; redacted_spans: number[] }
  // `ids` is backend-side attribution (which memory items shaped the turn, for
  // joining against ratings) and is not rendered.
  | { type: "user_memory_applied"; count: number; texts?: string[]; ids?: string[] }
  | { type: "truncated"; reason: "max_tokens" | "refusal" }
  | {
      type: "report_ready";
      report_id: string;
      revision_number: number;
      title: string;
      status: "ready" | "partial";
      assistant_message_id: string;
    }
  | {
      type: "report_publish_failed";
      trigger: "edit" | "explicit" | "background" | "autonomous" | null;
      reason: "publication_failed" | "control_not_published";
    }
  | {
      type: "story_preparation_ready";
      preparation_id: string;
      revision_number: number;
      state: PreparationState;
      title: string;
      operation: Operation;
      target_story_public_id: number | null;
    }
  | {
      type: "done";
      citation_count: number;
      /** Persisted assistant row id, so the live client can target it
       * (answer rating, etc.) without a session reload. Optional for
       * backwards compatibility with older backends. */
      assistant_message_id?: string;
    }
  | {
      type: "error";
      message: string;
      /** Machine-readable failure code (e.g. `"generation_failed"`,
       * `"rate_limited"`, `"turn_timeout"`,
       * `"tool_iteration_cap_exceeded"`). `"generation_failed"` and
       * `"rate_limited"` get a retry action; the others stay plain
       * notices — see `recover-chat-turn-hard-failures` and
       * `harden-self-knowledge-seeding-and-rate-limit-ux`. */
      code?: string;
    }
  | {
      type: "response_assets";
      assistant_message_id: string;
      /** Ordered, non-empty per the backend contract — emitted only when at
       * least one `chat_message_assets` relation committed with this turn. */
      assets: ResponseAssetProjection[];
    };

export type FactProposalStatus = "pending" | "approved" | "cancelled";
export type FactProposalKind = "fact" | "rule";
export type UserMemoryDraftOperation = "create" | "update" | "delete";
export type UserMemoryDraftStatus = "pending" | "approved" | "cancelled";

export interface FactProposalState {
  draft_id: string;
  text: string;
  status: FactProposalStatus;
  detected_at: string;
  kind?: FactProposalKind;
  scope?: string;
  decided_at?: string;
  knowledge_item_id?: string;
  /** Present in live SSE events; on reload, hydrated from msg.id.
   * Used by FactProposalCardWithContext so lifecycle endpoints receive
   * the real chat_messages.id instead of the client-side turn UUID. */
  assistant_message_id?: string;
  /** The text the proposal had before the latest refinement that changed it.
   * Client state only: the server keeps just the latest text, so a card read back
   * after a reload has none and offers no comparison. Set by `withRefinedText`. */
  previousText?: string;
}

/** The proposal after a refinement set its text to `text`. A refinement that changes
 * the text keeps the text it replaced in `previousText` (only the latest one is kept);
 * one that leaves it unchanged returns the proposal as it was, `previousText` included. */
export function withRefinedText(proposal: FactProposalState, text: string): FactProposalState {
  if (text === proposal.text) return proposal;
  return { ...proposal, text, previousText: proposal.text };
}

export interface UserMemoryDraftState {
  draft_id: string;
  operation: UserMemoryDraftOperation;
  status: UserMemoryDraftStatus;
  detected_at: string;
  text?: string;
  target_id?: string;
  target_text?: string;
  decided_at?: string;
  memory_item_id?: string;
  assistant_message_id?: string;
}

interface StreamOptions {
  message: string;
  sessionId?: string | undefined;
  signal?: AbortSignal | undefined;
  modelOverride?: { provider: string; model_identifier: string } | undefined;
  reasoningLevel?: ReasoningPreference | undefined;
  attachmentIds?: readonly string[] | undefined;
  activeReport?: ReportPointer | undefined;
  reportEditBase?: ReportPointer | undefined;
}

/**
 * Best-effort text extraction for a non-2xx `/chat` response body that
 * doesn't match a specific typed error shape (cost-limit, message-too-long),
 * so the generic fallback in `chatErrorFor` surfaces backend detail text
 * instead of collapsing to the bare HTTP status. Checks, in order: a bare
 * string `detail`, `detail.detail`, then `detail.message` — whichever the
 * specific backend error populated (see design.md §8 of
 * `raise-and-surface-chat-message-cap`).
 */
function extractChatErrorDetailText(payload: unknown): string | null {
  if (!payload || typeof payload !== "object" || !("detail" in payload)) return null;
  const detail = (payload as { detail: unknown }).detail;
  if (typeof detail === "string" && detail.length > 0) return detail;
  if (detail && typeof detail === "object") {
    const nestedDetail = (detail as { detail?: unknown }).detail;
    if (typeof nestedDetail === "string" && nestedDetail.length > 0) return nestedDetail;
    const message = (detail as { message?: unknown }).message;
    if (typeof message === "string" && message.length > 0) return message;
  }
  return null;
}

/** Build the error to throw for a non-ok `POST /chat` response. A 429 carrying
 * `detail.code="llm_cost_limit_exceeded"` becomes a typed `LlmCostLimitError`
 * (so the route renders a scope-specific notice); a 422 carrying
 * `detail.code="message_too_long"` becomes a typed `MessageTooLongError`.
 * Any other non-ok response surfaces the backend's `detail` text (bare
 * string, or nested `detail.detail`/`detail.message`) when present, falling
 * back to the bare status line otherwise. Reads the body as text so a
 * malformed payload never masks the status. */
async function chatErrorFor(response: Response): Promise<Error> {
  const text = await response.text();
  let payload: unknown = text;
  try {
    payload = JSON.parse(text);
  } catch {
    // keep raw text
  }
  const scope = parseLlmCostLimitScope(payload);
  if (scope) return new LlmCostLimitError(scope);
  const tooLong = parseMessageTooLongDetail(payload);
  if (tooLong) return new MessageTooLongError(tooLong.maxChars, tooLong.actualChars);
  const detailText = extractChatErrorDetailText(payload);
  if (detailText) {
    return new Error(`Chat request failed: ${response.status} — ${detailText}`);
  }
  return new Error(`Chat request failed: ${response.status} ${response.statusText}`);
}

/**
 * Stream `/chat` SSE events as an async iterable. Caller decides how to
 * render each event; the helper just parses `event:` + `data:` lines into
 * typed objects and yields them in order.
 */
export async function* streamChat(opts: StreamOptions): AsyncIterable<ChatStreamEvent> {
  const activeReport =
    opts.activeReport === undefined ? undefined : reportPointerSchema.parse(opts.activeReport);
  const reportEditBase =
    opts.reportEditBase === undefined ? undefined : reportPointerSchema.parse(opts.reportEditBase);
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const headers: Record<string, string> = {
    Accept: "text/event-stream",
    "Content-Type": "application/json",
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const init: RequestInit = {
    method: "POST",
    headers,
    body: JSON.stringify({
      message: opts.message,
      session_id: opts.sessionId,
      ...(opts.modelOverride ? { model_override: opts.modelOverride } : {}),
      ...(opts.reasoningLevel ? { reasoning_level: opts.reasoningLevel } : {}),
      ...(opts.attachmentIds && opts.attachmentIds.length > 0
        ? { attachment_ids: opts.attachmentIds }
        : {}),
      ...(activeReport ? { active_report: activeReport } : {}),
      ...(reportEditBase ? { report_edit_base: reportEditBase } : {}),
    }),
  };
  if (opts.signal) init.signal = opts.signal;
  const response = await fetch(`${baseUrl}/chat`, init);

  if (!response.ok) throw await chatErrorFor(response);
  if (!response.body) {
    throw new Error(`Chat request failed: ${response.status} ${response.statusText}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  // SSE event boundary is a blank line. The spec allows \n, \r, or \r\n
  // as line endings, so the boundary may be \n\n, \r\n\r\n, or \r\r.
  const boundary = /\r\n\r\n|\n\n|\r\r/;
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let match = boundary.exec(buffer);
    while (match) {
      const block = buffer.slice(0, match.index);
      buffer = buffer.slice(match.index + match[0].length);
      const event = parseSseBlock(block);
      if (event) yield event;
      match = boundary.exec(buffer);
    }
  }
}

/**
 * Reconnect to an active generation stream for a session.
 * Reads buffered events (catch-up) then live deltas until the generation ends.
 */
export async function* streamSessionEvents(
  sessionId: string,
  opts?: { signal?: AbortSignal },
): AsyncIterable<ChatStreamEvent> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const headers: Record<string, string> = {
    Accept: "text/event-stream",
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(`${baseUrl}/chat/sessions/${sessionId}/stream`, {
    method: "GET",
    headers,
    signal: opts?.signal ?? null,
  });

  if (!response.ok || !response.body) {
    throw new Error(`Stream request failed: ${response.status} ${response.statusText}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  const boundary = /\r\n\r\n|\n\n|\r\r/;
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let match = boundary.exec(buffer);
    while (match) {
      const block = buffer.slice(0, match.index);
      buffer = buffer.slice(match.index + match[0].length);
      const event = parseSseBlock(block);
      if (event) yield event;
      match = boundary.exec(buffer);
    }
  }
}

/**
 * Cancel an active generation for a session.
 * Idempotent: returns successfully even if no generation is active.
 */
export async function cancelGeneration(sessionId: string): Promise<void> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(`${baseUrl}/chat/sessions/${sessionId}/generating`, {
    method: "DELETE",
    headers,
  });

  if (!response.ok && response.status !== 204) {
    throw new Error(`Cancel request failed: ${response.status} ${response.statusText}`);
  }
}

// --------------------------------------------------------------------------- //
// Background-research live delivery (`add-background-research-agent` Layer 2)   //
// --------------------------------------------------------------------------- //

/** A terminal-task notification pushed over the per-session SSE stream when a
 * background research task finishes. Lightweight by design — the client
 * refetches the session messages to render the persisted report. */
export interface BackgroundTaskEvent {
  type: "background_task";
  task_id: string;
  status: "done" | "truncated" | "cancelled" | "failed";
  result_message_id: string | null;
}

export interface BackgroundProgressEvent {
  type: "background_progress";
  task_id: string;
  status: "running";
  content: string;
  model?: string | undefined;
  steps: {
    id: string;
    status: "running" | "done" | "error";
    elapsed_ms?: number | undefined;
    detail?:
      | {
          hits?: number | undefined;
          bytes?: number | undefined;
          stage?: string | undefined;
          fused?: number | undefined;
          error?: string | undefined;
          decision?: string | undefined;
          reason?: string | undefined;
        }
      | undefined;
  }[];
  tool_calls: {
    id: string;
    name: string;
    input: Record<string, unknown>;
    ok?: boolean | undefined;
    bytes?: number | undefined;
    content_preview?: string | undefined;
    error?: string | undefined;
    ref?: string | undefined;
    stage?: string | undefined;
    ref_fallback?: boolean | undefined;
    tried_refs?: string[] | undefined;
  }[];
  citations: {
    id: string;
    title: string;
    preview: string;
    user_metadata: string | null;
    source?: string | undefined;
  }[];
}

export type BackgroundSessionEvent = BackgroundTaskEvent | BackgroundProgressEvent;

const sessionEventSchema = z.object({
  type: z.literal("background_task"),
  task_id: z.string(),
  status: z.enum(["done", "truncated", "cancelled", "failed"]),
  result_message_id: z.string().nullable(),
});

const backgroundProgressCitationSchema = z.object({
  id: z.string(),
  title: z.string(),
  preview: z.string(),
  user_metadata: z.string().nullable(),
  source: z.string().optional(),
});

const backgroundProgressEventSchema = z.object({
  type: z.literal("background_progress"),
  task_id: z.string(),
  status: z.literal("running"),
  content: z.string(),
  model: z.string().optional(),
  steps: z.array(
    z.object({
      id: z.string(),
      status: z.enum(["running", "done", "error"]),
      elapsed_ms: z.number().int().nonnegative().optional(),
      detail: z
        .object({
          hits: z.number().int().nonnegative().optional(),
          bytes: z.number().int().nonnegative().optional(),
          stage: z.string().optional(),
          fused: z.number().int().nonnegative().optional(),
          error: z.string().optional(),
          decision: z.string().optional(),
          reason: z.string().optional(),
        })
        .optional(),
    }),
  ),
  tool_calls: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      input: z.record(z.string(), z.unknown()),
      ok: z.boolean().optional(),
      bytes: z.number().optional(),
      content_preview: z.string().optional(),
      error: z.string().optional(),
      ref: z.string().optional(),
      stage: z.string().optional(),
      ref_fallback: z.boolean().optional(),
      tried_refs: z.array(z.string()).optional(),
    }),
  ),
  citations: z.array(backgroundProgressCitationSchema),
});

const backgroundSessionEventSchema = z.union([sessionEventSchema, backgroundProgressEventSchema]);

/** Parse one SSE block from the session-events stream into a typed
 * `BackgroundTaskEvent`, or `null` for keepalive pings / unknown shapes.
 * Exported for unit testing the wire contract. */
export function parseSessionEvent(block: string): BackgroundSessionEvent | null {
  const data = block
    .split("\n")
    .filter((l) => l.startsWith("data:"))
    .map((l) => l.slice("data:".length).trim())
    .join("\n");
  if (!data) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(data);
  } catch {
    return null;
  }
  const result = backgroundSessionEventSchema.safeParse(parsed);
  return result.success ? result.data : null;
}

/**
 * Subscribe to `GET /chat/sessions/{id}/events` (live background-research
 * notifications + reconnect catch-up). Uses fetch + ReadableStream rather than
 * `EventSource` because the endpoint authenticates via the `Authorization`
 * header. Yields one event per terminal task; keepalive pings are swallowed.
 */
export async function* subscribeSessionEvents(
  sessionId: string,
  opts?: { signal?: AbortSignal | undefined },
): AsyncIterable<BackgroundSessionEvent> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const headers: Record<string, string> = { Accept: "text/event-stream" };
  if (token) headers.Authorization = `Bearer ${token}`;

  const init: RequestInit = { method: "GET", headers };
  if (opts?.signal) init.signal = opts.signal;
  const response = await fetch(`${baseUrl}/chat/sessions/${sessionId}/events`, init);
  if (!response.ok || !response.body) {
    throw new Error(`Session events subscription failed: ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  const boundary = /\r\n\r\n|\n\n|\r\r/;
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let match = boundary.exec(buffer);
    while (match) {
      const block = buffer.slice(0, match.index);
      buffer = buffer.slice(match.index + match[0].length);
      const event = parseSessionEvent(block);
      if (event) yield event;
      match = boundary.exec(buffer);
    }
  }
}

const cancelBackgroundTaskResponseSchema = z.object({ status: z.string() });

/** Request cancellation of an in-flight background research task. The worker
 * checks the flag between steps and finalizes with the partial report. */
export async function cancelBackgroundTask(taskId: string): Promise<{ status: string }> {
  return apiClient.post(
    `/chat/background-tasks/${taskId}/cancel`,
    cancelBackgroundTaskResponseSchema,
    {},
  );
}

/** The user's single active conversational session, if any. `reason`
 * distinguishes a running investigation (`in_flight`) from a merely-subscribed
 * tab (`subscribed`). `null` when there is nothing to resume. */
export const activeSessionSchema = z
  .object({
    session_id: z.guid(),
    reason: z.enum(["in_flight", "subscribed"]),
  })
  .nullable();
export type ActiveSession = z.infer<typeof activeSessionSchema>;

export const ACTIVE_SESSION_QUERY_KEY = ["chat", "active-session"] as const;

/** GET /chat/active-session — the session to offer to resume on return. */
export async function getActiveSession(): Promise<ActiveSession> {
  return apiClient.get("/chat/active-session", activeSessionSchema);
}

// --------------------------------------------------------------------------- //
// Session history reads (`add-chat-history-read`)                              //
// --------------------------------------------------------------------------- //

export const chatSessionSummarySchema = z.object({
  id: z.guid(),
  started_at: z.string(),
  last_message_at: z.string(),
  message_count: z.number().int().nonnegative(),
  title: z.string().nullable(),
  origin: z.enum(["web", "slack", "mcp"]).optional().default("web"),
  title_is_custom: z.boolean().optional().default(false),
  archived_at: z.string().nullable().optional().default(null),
  has_pending_chat_fact: z.boolean().optional().default(false),
  has_approved_chat_fact: z.boolean().optional().default(false),
});
export type ChatSessionSummary = z.infer<typeof chatSessionSummarySchema>;

/**
 * Max length of a heuristic session title, mirroring the backend's
 * `TITLE_MAX_LEN` in `chat_store.py`. The optimistic title shown the instant a
 * new chat's first message is sent must match the server-side `truncate_title`
 * rule so the value doesn't visibly change when the sidebar refetches the
 * persisted row.
 */
export const SESSION_TITLE_MAX_LEN = 60;

/**
 * Heuristic title for a brand-new session, mirroring the backend
 * `truncate_title`: trim to decide emptiness, then slice the ORIGINAL content to
 * `SESSION_TITLE_MAX_LEN` (no ellipsis). Whitespace-only and empty input surface
 * as `null` so the sidebar renders its empty state.
 */
export function optimisticSessionTitle(content: string): string | null {
  if (content.trim().length === 0) return null;
  return content.slice(0, SESSION_TITLE_MAX_LEN);
}

/**
 * Build the optimistic `ChatSessionSummary` inserted into the sidebar the
 * instant a new chat's first message is sent, before the backend has persisted
 * the row. `id` is the pending session id the client already minted (and which
 * the backend materialises the session under), so the post-stream sessions
 * invalidation reconciles against the same id with no duplicate, and the naming
 * worker replaces the title later.
 */
export function buildOptimisticSession(args: {
  id: string;
  message: string;
  nowIso: string;
}): ChatSessionSummary {
  return {
    id: args.id,
    started_at: args.nowIso,
    last_message_at: args.nowIso,
    message_count: 1,
    title: optimisticSessionTitle(args.message),
    origin: "web",
    title_is_custom: false,
    archived_at: null,
    has_pending_chat_fact: false,
    has_approved_chat_fact: false,
  };
}

/**
 * Prepend an optimistic session summary to a cached active-sessions list. No-op
 * when the list isn't cached yet (`undefined` — the sidebar query will fetch it
 * fresh) or when a row with the same id is already present (idempotent against
 * re-sends or a refetch that already landed).
 */
export function prependOptimisticSession(
  old: ChatSessionSummary[] | undefined,
  summary: ChatSessionSummary,
): ChatSessionSummary[] | undefined {
  if (old === undefined) return old;
  if (old.some((s) => s.id === summary.id)) return old;
  return [summary, ...old];
}

const chatSessionListSchema = z.array(chatSessionSummarySchema);

const citationReadSchema = z.object({
  id: z.string(),
  title: z.string(),
  preview: z.string().optional(),
  user_metadata: z.string().nullable().optional(),
  source: z.string().optional(),
  source_ref: z.string().optional(),
  external_url: z.string().optional(),
});

/**
 * Canonical citation shape mirroring the backend `Citation` Pydantic
 * model (`src/rag/types.py`) plus the `source` field carried over the
 * `add-self-knowledge-source` SSE contract. This is the typed schema
 * consumers should use when they need to discriminate `self` vs `doc`
 * citation rendering. Distinct from `citationReadSchema`, which
 * preserves the legacy wire shape used by history reads (`id`-keyed).
 */
export const citationSchema = z.object({
  knowledge_item_id: z.guid(),
  title: z.string(),
  preview: z.string(),
  user_metadata: z.string().nullable(),
  source: sourceKindSchema,
});
export type Citation = z.infer<typeof citationSchema>;

/**
 * A single selectable option carried by a `user_questions` question (or, in
 * the legacy shape, a `knowledge_options` block). Shared by both schemas
 * below.
 */
export const knowledgeOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
});

/**
 * Legacy payload carried by a ```knowledge_options fenced block, emitted by
 * historical guided knowledge-training turns (see
 * `openspec/changes/add-guided-knowledge-training-flow/design.md`, section
 * "Selectable Answer Options"). The prompt no longer instructs the model to
 * emit this shape — new output uses `user_questions` (see
 * `userQuestionsSchema` below) — but the frontend keeps parsing it as a
 * render-only alias so historical turns keep rendering. `mode: "single"`
 * sends one option's label immediately on click; `mode: "multi"` toggles
 * chips and sends the combined selection on an explicit confirm action.
 * Malformed payloads fall back to a plain code block, so callers should use
 * `.safeParse()`. Normalize a parsed payload to a `UserQuestions` group with
 * `normalizeKnowledgeOptions` below before rendering.
 */
export const knowledgeOptionsSchema = z.object({
  question_id: z.string(),
  mode: z.enum(["single", "multi"]),
  options: z.array(knowledgeOptionSchema).min(1),
});
export type KnowledgeOptions = z.infer<typeof knowledgeOptionsSchema>;

/**
 * Payload carried by a ```user_questions fenced block — the generic
 * selectable-question transport the assistant may emit in any chat turn
 * (see `openspec/changes/generalize-selectable-user-questions/design.md`,
 * section "Fence contract"). Rendered by `UserQuestionsFence` in
 * `frontend/src/routes/chat.tsx`, same fence pattern as ```mermaid and the
 * legacy ```knowledge_options. A single question renders inline chips
 * (`single` sends on click, `multi` toggles + confirm); two or more
 * questions render a tabbed widget where each question is answered locally
 * and the whole group is submitted as one combined message. `header` is an
 * optional short tab label (~16 chars target); when missing or blank the
 * frontend falls back to `Pregunta ${index + 1}`. Malformed payloads fall
 * back to a plain code block, so callers should use `.safeParse()`.
 */
export const userQuestionSchema = z.object({
  id: z.string(),
  question: z.string(),
  header: z.string().optional(),
  mode: z.enum(["single", "multi"]),
  options: z.array(knowledgeOptionSchema).min(1),
});
export const userQuestionsSchema = z.object({
  questions: z.array(userQuestionSchema).min(1),
});
export type UserQuestion = z.infer<typeof userQuestionSchema>;
export type UserQuestions = z.infer<typeof userQuestionsSchema>;

/**
 * Normalizes a parsed legacy `knowledge_options` payload into a one-question
 * `UserQuestions` group, so `UserQuestionsFence` can render both fence
 * shapes through a single code path. The question text is left blank — the
 * assistant's prose already carries the question in the legacy flow — and
 * no `header` is set (only meaningful for multi-question groups).
 */
export function normalizeKnowledgeOptions(legacy: KnowledgeOptions): UserQuestions {
  return {
    questions: [
      {
        id: legacy.question_id,
        question: "",
        mode: legacy.mode,
        options: legacy.options,
      },
    ],
  };
}

const reasoningLevelSchema = z.enum(["off", "low", "medium", "high"]);
const reasoningPreferenceSchema = z.enum(["auto", "off", "low", "medium", "high"]);

// One persisted pipeline step. Validated per-element (see `steps` below) so a
// single malformed step never blanks the whole stepper on reload.
const persistedStepSchema = z.object({
  id: z.string(),
  status: z.enum(["done", "error"]),
  // Optional: decision-style steps (e.g. retrieval.graph_routing) run
  // overlapped in the parallel phase and carry no wall-clock of their
  // own. Requiring it here made a single such step fail the whole
  // metadata safeParse, so the stepper never rehydrated on reload.
  elapsed_ms: z.number().int().nonnegative().optional(),
  detail: z
    .object({
      hits: z.number().int().nonnegative().optional(),
      bytes: z.number().int().nonnegative().optional(),
      stage: z.string().optional(),
      fused: z.number().int().nonnegative().optional(),
      error: z.string().optional(),
      decision: z.string().optional(),
      reason: z.string().optional(),
      tier: z.string().optional(),
      selected: z
        .object({
          provider: z.string(),
          model_id: z.string(),
          display_name: z.string().optional(),
        })
        .optional(),
      shadow: z.boolean().optional(),
      sticky: z.boolean().optional(),
      would_skip: z.boolean().optional(),
      skipped: z.boolean().optional(),
      // question_classification: the resolved intent and who decided it.
      intent: z.string().optional(),
      decided_by: z.string().optional(),
    })
    .optional(),
  usage: z
    .object({
      // Optional for back-compat: rows persisted before slot tracking omit it.
      slot: z.string().optional(),
      model: z.string(),
      prompt_tokens: z.number(),
      completion_tokens: z.number(),
      total_tokens: z.number(),
      cost_usd: z.string().nullable(),
      cost_source: z.enum(["computed", "unknown"]),
      // Optional for back-compat: rows persisted before cache tokens were
      // tracked omit them. Summed across the step's rounds by the backend.
      cache_read_input_tokens: z.number().optional(),
      cache_creation_input_tokens: z.number().optional(),
      // Stamped true on the `answer` step when a failover fallback leg
      // served (fix-served-model-attribution-on-failover). Absent on
      // primary-served turns and historical rows.
      fallback_used: z.boolean().optional(),
      // Every provider call of the step; only present when a decision engine
      // took part. Historical rows omit it. Non-strict like the rest of the
      // persisted shape, so a future call field never drops the whole step.
      calls: z.array(stepCallWireSchema.strip()).optional(),
    })
    .optional(),
});

export const assistantMetadataSchema = z
  .object({
    model: z
      .object({
        provider: z.string(),
        model_id: z.string(),
        display_name: z.string().optional(),
        // Stamped true when a per-slot provider failover fallback leg
        // served the turn (see fix-served-model-attribution-on-failover).
        // Absent/false on primary-served and historical rows.
        fallback_used: z.boolean().optional(),
      })
      .optional(),
    usage: z
      .object({
        prompt_tokens: z.number(),
        completion_tokens: z.number(),
        total_tokens: z.number(),
        cost_usd: z.number().optional(),
        // Per-turn prompt-cache input tokens. Optional for back-compat:
        // rows persisted before cache tokens were tracked omit them.
        cache_read_input_tokens: z.number().optional(),
        cache_creation_input_tokens: z.number().optional(),
      })
      .optional(),
    retrieval: z
      .object({
        vector: z.number(),
        bm25: z.number(),
        graph: z.number(),
        // Added with the learned-sparse leg; older persisted turns omit it.
        sparse: z.number().optional(),
        fused: z.number(),
      })
      .optional(),
    reasoning_level: reasoningLevelSchema.optional(),
    reasoning_preference: reasoningPreferenceSchema.optional(),
    report_ref: reportRefSchema.optional(),
    story_preparation: storyPreparationRefSchema.optional(),
    tool_calls: z
      .array(
        z.object({
          id: z.string(),
          name: z.string(),
          input: z.record(z.string(), z.unknown()),
          ok: z.boolean(),
          bytes: z.number(),
          content_preview: z.string(),
          error: z.string().optional(),
          ref: z.string().optional(),
          stage: z.string().optional(),
          ref_fallback: z.boolean().optional(),
          tried_refs: z.array(z.string()).optional(),
        }),
      )
      .optional(),
    // Citation markers (`[doc-N]` / `[self-N]`) the model emitted but
    // referenced an N outside the retrieval budget. The backend strips
    // them from the answer and surfaces the list here for the UI to
    // render a warning. Persisted so the chip survives a session
    // reload — see PR #223 (backend) for the streaming filter.
    fabricated_citations: z.array(z.string()).optional(),
    // Per-turn counts of verbatim source spans the regurgitation filter
    // redacted from a user without `view_source`. Drives a one-line
    // warning; persisted so it survives a reload. See
    // `add-source-access-capability-gating`.
    source_redactions: z.array(z.number()).optional(),
    // Read-side memory transparency: how many user-memory items were injected
    // into this turn's context, plus the injected texts — a deliberate snapshot
    // for historical fidelity (the panel shows them even if the items are later
    // edited or deleted). Shown in the details panel; persisted so it survives
    // a reload. See `show-applied-memory-texts`.
    user_memory_applied: z.number().optional(),
    user_memory_applied_texts: z.array(z.string()).optional(),
    // Ids of the same items, same order — attribution only (they let a turn be
    // traced back to the memory that shaped it once the consolidation pass has
    // rewritten the text). Not rendered; absent on turns persisted before
    // `harden-user-memory-capture-and-injection`.
    user_memory_applied_ids: z.array(z.string()).optional(),
    // Abnormal stop reason of the final generation round: the answer was cut
    // by the output token budget ("max_tokens") or the model refused
    // ("refusal"). The UI renders a notice on the message; persisted so it
    // survives a session reload. See `add-chat-output-budget-and-truncation-notice`.
    truncated: z.enum(["max_tokens", "refusal"]).optional(),
    // Set true on the assistant row when the user cancelled the turn mid-stream
    // (`add-chat-turn-cancellation`). The row holds the partial answer; the UI
    // renders a "cancelled" indicator instead of an error on reload.
    cancelled: z.boolean().optional(),
    // Set true while the detached generation task is still writing partial
    // content into this assistant row. Cleared by the final update.
    generating: z.boolean().optional(),
    // Set true on an assistant stub the reaper finalized after the turn was
    // abandoned (empty content), or on any other terminal failure that left
    // partial content. The UI renders a muted notice instead of an empty
    // bubble when `failed` is true and content is empty; a non-empty
    // `failed` message keeps rendering its content unchanged. See
    // `surface-abandoned-plugin-turns`.
    failed: z.boolean().optional(),
    // Machine-readable reason paired with `failed`, keyed by the UI to pick
    // notice copy — `"plugin_turn_abandoned"` for the reaper's terminal
    // state, any other value (or absence) falls back to a generic notice.
    // See `surface-abandoned-plugin-turns`.
    error: z.string().optional(),
    // Set true when the orchestrator had to substitute a fallback message
    // for what would otherwise be an empty assistant row (provider error,
    // cancellation before the first token, or an unhandled exception). The
    // persisted `content` is already the fallback text — this flag only
    // drives the retry affordance the UI renders alongside it. See
    // `recover-chat-turn-hard-failures`.
    generation_failed: z.boolean().optional(),
    // Free-text failure cause paired with `generation_failed` (e.g.
    // `"provider_error"`, `"turn_timeout"`), shown in no UI copy today —
    // reserved for future diagnostics. See `recover-chat-turn-hard-failures`.
    // Persisted by the backend as `metadata.reason`.
    reason: z.string().optional(),
    // Background-research report metadata (`add-background-research-agent`).
    // Present only on the assistant message a background task persists: it
    // carries the originating task id (used to link the report back to the
    // turn that delegated it) and the sub-agent's accumulated LLM usage so
    // the report can render a model/cost footer. `background_usage` is absent
    // when the task failed before any LLM call (e.g. pool not configured).
    background_task_id: z.string().optional(),
    background_status: z.enum(["done", "truncated", "cancelled", "failed"]).optional(),
    background_report: z
      .object({
        summary: z.string(),
        full_report: z.string(),
        full_report_status: z.enum(["complete", "truncated"]),
        truncation_reason: z.string().optional(),
      })
      .optional(),
    // Run wall-clock of the sub-agent, shown as the final time on the
    // investigations bar (survives reload — the live counter ticks client-side
    // only while running).
    background_elapsed_seconds: z.number().optional(),
    background_usage: z
      .object({
        model: z.string(),
        prompt_tokens: z.number(),
        completion_tokens: z.number(),
        total_tokens: z.number(),
        cost_usd: z.number(),
      })
      .optional(),
    // Persisted fact-proposal metadata so the card state survives a
    // session reload via GET /chat/sessions/{id}/messages.
    fact_proposal: z
      .object({
        draft_id: z.string(),
        text: z.string(),
        status: z.enum(["pending", "approved", "cancelled"]),
        detected_at: z.string(),
        kind: z.enum(["fact", "rule"]).optional(),
        scope: z.string().optional(),
        decided_at: z.string().optional(),
        knowledge_item_id: z.guid().optional(),
        // Persisted so the card can use the real chat_messages.id on
        // reload. Optional — rows created before this field was added
        // fall back to msg.id during hydration in messageToTurns.
        assistant_message_id: z.guid().optional(),
      })
      .optional(),
    fact_proposals: z
      .array(
        z.object({
          draft_id: z.string(),
          text: z.string(),
          status: z.enum(["pending", "approved", "cancelled"]),
          detected_at: z.string(),
          kind: z.enum(["fact", "rule"]).optional(),
          scope: z.string().optional(),
          decided_at: z.string().optional(),
          knowledge_item_id: z.guid().optional(),
          assistant_message_id: z.guid().optional(),
        }),
      )
      .optional(),
    user_memory_draft: z
      .object({
        draft_id: z.string(),
        operation: z.enum(["create", "update", "delete"]),
        status: z.enum(["pending", "approved", "cancelled"]),
        detected_at: z.string(),
        text: z.string().optional(),
        target_id: z.guid().optional(),
        target_text: z.string().optional(),
        decided_at: z.string().optional(),
        memory_item_id: z.guid().optional(),
        assistant_message_id: z.guid().optional(),
      })
      .optional(),
    user_memory_drafts: z
      .array(
        z.object({
          draft_id: z.string(),
          operation: z.enum(["create", "update", "delete"]),
          status: z.enum(["pending", "approved", "cancelled"]),
          detected_at: z.string(),
          text: z.string().optional(),
          target_id: z.guid().optional(),
          target_text: z.string().optional(),
          decided_at: z.string().optional(),
          memory_item_id: z.guid().optional(),
          assistant_message_id: z.guid().optional(),
        }),
      )
      .optional(),
    model_routing: z
      .object({
        reason: z.string(),
        shadow: z.boolean(),
        sticky: z.boolean(),
        would_skip: z.boolean(),
        tier: z.string().optional(),
        selected: z
          .object({
            provider: z.string(),
            model_id: z.string(),
          })
          .optional(),
      })
      .optional(),
    steps: z
      .array(z.unknown())
      .transform((raw) =>
        raw.flatMap((candidate, index) => {
          const result = persistedStepSchema.safeParse(candidate);
          if (result.success) return [result.data];
          // Per-step tolerance: drop just the malformed step (a future
          // backend field, a bad enum) instead of failing the whole metadata
          // parse and blanking the stepper. Surface it in dev so
          // backend/frontend shape drift doesn't pass silently.
          if (import.meta.env.DEV) {
            console.warn("[chat] dropping invalid persisted step during rehydration", {
              index,
              issues: result.error.issues,
            });
          }
          return [];
        }),
      )
      .optional(),
  })
  .passthrough();
export type AssistantMetadata = z.infer<typeof assistantMetadataSchema>;

export const chatMessageAttachmentSchema = z.object({
  id: z.guid(),
  filename: z.string(),
  kind: attachmentKindSchema,
  status: attachmentStatusSchema,
});
export type ChatMessageAttachment = z.infer<typeof chatMessageAttachmentSchema>;

const answerRatingValueSchema = z.enum(["up", "down"]);
export const answerRatingReasonSchema = z.enum(["incorrect", "outdated", "contradictory", "other"]);
export const knowledgeAnalysisStatusSchema = z.enum(["pending", "running", "succeeded", "failed"]);
export const knowledgeVerdictSchema = z.enum(["supported", "not_supported", "inconclusive"]);
export const answerRatingSchema = z.object({
  message_id: z.guid(),
  rating: answerRatingValueSchema,
  reason_type: answerRatingReasonSchema.nullable().optional(),
  reason: z.string().nullable(),
  knowledge_report_id: z.guid().nullable().optional(),
  knowledge_analysis_status: knowledgeAnalysisStatusSchema.nullable().optional(),
  knowledge_verdict: knowledgeVerdictSchema.nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type AnswerRating = z.infer<typeof answerRatingSchema>;
export type AnswerRatingValue = z.infer<typeof answerRatingValueSchema>;

export const chatKnowledgeFeedbackSchema = z.object({
  report_id: z.guid(),
  analysis_status: knowledgeAnalysisStatusSchema,
  verdict: knowledgeVerdictSchema.nullable().optional(),
});
export type ChatKnowledgeFeedback = z.infer<typeof chatKnowledgeFeedbackSchema>;

export const chatMessageReadSchema = z.object({
  id: z.guid(),
  role: z.enum(["user", "assistant", "system"]),
  content: z.string(),
  created_at: z.string(),
  citations: z.array(citationReadSchema).nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  attachments: z.array(chatMessageAttachmentSchema).optional().default([]),
  // Safe knowledge-asset projections attached to this assistant message —
  // top-level sibling of `attachments`/`citations`, never a bag inside
  // `metadata`. Populated identically by `GET .../messages` and
  // `GET .../messages/{id}` so the live SSE event and a session reload agree.
  // See `return-knowledge-images-across-surfaces` PR 2, chat spec
  // requirement "Assistant messages persist and stream typed knowledge
  // assets".
  response_assets: z.array(responseAssetProjectionSchema).optional().default([]),
  // Populated asynchronously by the `generate_minimap_labels` ARQ job
  // (see `add-chat-minimap-and-utility-slot`). `null` while the job
  // is pending, terminally failed, or the row pre-dates the migration —
  // the chat UI falls back to a heuristic truncation of `content` so
  // the rail is never empty.
  minimap_label: z.string().nullable().optional(),
  answer_rating: answerRatingSchema.nullable().optional(),
  knowledge_feedback: chatKnowledgeFeedbackSchema.nullable().optional(),
});
export type ChatMessageRead = z.infer<typeof chatMessageReadSchema>;

const chatMessageListSchema = z.array(chatMessageReadSchema);

const VOID = z.unknown().transform(() => undefined);

export async function listSessions(opts?: {
  limit?: number;
  offset?: number;
  archived?: boolean;
}): Promise<ChatSessionSummary[]> {
  const query: Record<string, number | boolean> = {};
  if (opts?.limit !== undefined) query.limit = opts.limit;
  if (opts?.offset !== undefined) query.offset = opts.offset;
  if (opts?.archived !== undefined) query.archived = opts.archived;
  return apiClient.get("/chat/sessions", chatSessionListSchema, { query });
}

export async function getMessages(sessionId: string): Promise<ChatMessageRead[]> {
  return apiClient.get(`/chat/sessions/${sessionId}/messages`, chatMessageListSchema);
}

/** React Query key for a session's persisted message list (cache-first session
 * switching, `optimize-chat-session-loading` phase 1). Shared by the chat route
 * (hydration + refresh writes) and the sidebar (hover prefetch) so cache reads
 * and writes use the same key. */
export const messagesQueryKey = (sessionId: string) => ["chat", "messages", sessionId] as const;

/** Staleness window for the cached message list. Returning to a session within
 * this window renders from cache with no blocking refetch; a background refetch
 * runs only once the cached data is older than this. */
export const MESSAGES_STALE_MS = 60_000;

export async function getMessageDetail(
  sessionId: string,
  messageId: string,
): Promise<ChatMessageRead> {
  return apiClient.get(`/chat/sessions/${sessionId}/messages/${messageId}`, chatMessageReadSchema);
}

export async function rateAnswer(
  messageId: string,
  body: { rating: AnswerRatingValue; reason_type?: "other" | null; reason?: string | null },
): Promise<AnswerRating> {
  return apiClient.post(`/chat/messages/${messageId}/rating`, answerRatingSchema, body);
}

export const knowledgeFeedbackRequestSchema = z.object({
  issue_type: z.enum(["incorrect", "outdated", "contradictory"]),
  explanation: z.string().trim().min(1).max(500),
});
export type KnowledgeFeedbackRequest = z.infer<typeof knowledgeFeedbackRequestSchema>;

export const knowledgeFeedbackSubmitResponseSchema = z.object({
  report_id: z.guid(),
  rating: answerRatingSchema,
  analysis_status: knowledgeAnalysisStatusSchema,
  created: z.boolean(),
});
export type KnowledgeFeedbackSubmitResponse = z.infer<typeof knowledgeFeedbackSubmitResponseSchema>;

export async function submitKnowledgeFeedback(
  messageId: string,
  body: KnowledgeFeedbackRequest,
): Promise<KnowledgeFeedbackSubmitResponse> {
  const parsed = knowledgeFeedbackRequestSchema.parse(body);
  return apiClient.post(
    `/chat/messages/${messageId}/knowledge-feedback`,
    knowledgeFeedbackSubmitResponseSchema,
    parsed,
  );
}

/** Remove the caller's rating for an assistant message. Idempotent. */
export async function unrateAnswer(messageId: string): Promise<void> {
  await apiClient.delete(`/chat/messages/${messageId}/rating`, VOID);
}

/** React Query key for the on-demand full payload of one message — the citation
 * `preview`/`user_metadata` that the list path defers (phase 2 of
 * optimize-chat-session-loading). One fetch warms every preview of the message. */
export const messageDetailQueryKey = (sessionId: string, messageId: string) =>
  ["chat", "message-detail", sessionId, messageId] as const;

const generatingSchema = z.object({ generating: z.boolean() });

/** Whether a chat turn is currently generating for this session. Lets a client
 * that (re)mounts mid-stream show a "Respondiendo…" indicator and refresh until
 * the assistant turn lands — the streamed answer isn't persisted until the
 * stream completes. See `add-chat-generating-indicator`. */
export async function getGenerating(sessionId: string): Promise<boolean> {
  const { generating } = await apiClient.get(
    `/chat/sessions/${sessionId}/generating`,
    generatingSchema,
  );
  return generating;
}

const generatingBatchSchema = z.record(z.string(), z.boolean());

/** Batched variant of {@link getGenerating}: the generating state for many
 * sessions in one request, keyed by session id. The sidebar uses this to drive
 * its per-session "Respondiendo…" dots without firing one request per session.
 * Ids are sent comma-joined; the response contains only the caller's owned
 * sessions (others are omitted, never disclosed). An empty list short-circuits
 * with no request. See `optimize-chat-sidebar-generating-poll`. */
export async function getGeneratingBatch(
  sessionIds: readonly string[],
): Promise<Record<string, boolean>> {
  if (sessionIds.length === 0) return {};
  return apiClient.get("/chat/sessions/generating", generatingBatchSchema, {
    query: { ids: sessionIds.join(",") },
  });
}

/** Show the synthetic "Respondiendo…" placeholder iff a turn is generating AND
 * the last persisted message is an unanswered user turn. Once a partial
 * assistant row exists, that row carries `metadata.generating=true` and is
 * rendered directly instead of adding a second placeholder. */
export function shouldShowGenerating(
  messages: readonly Pick<ChatMessageRead, "role" | "metadata">[],
  generating: boolean,
): boolean {
  if (!generating || messages.length === 0) return false;
  return messages[messages.length - 1]?.role === "user";
}

/** The placeholder can be retired (and polling stopped) once the assistant turn
 * has landed without an in-flight partial marker, or the generation marker
 * cleared. */
export function generationSettled(
  messages: readonly Pick<ChatMessageRead, "role" | "metadata">[],
  generating: boolean,
): boolean {
  const last = messages[messages.length - 1];
  const landed = last?.role === "assistant" && last.metadata?.generating !== true;
  return landed || !generating;
}

// --------------------------------------------------------------------------- //
// Session context inspector (`add-session-context-inspector`)                  //
// --------------------------------------------------------------------------- //

// `cost_usd` stays a string end-to-end — the backend serializes Decimal as a
// string to avoid float drift; rendering uses Intl.NumberFormat.
const slotUsageSchema = z.object({
  slot: z.string(),
  events: z.number().int(),
  total_tokens: z.number().int(),
  cost_usd: z.string(),
});

const modelUsageSchema = z.object({
  provider: z.string(),
  model_id: z.string(),
  events: z.number().int(),
  total_tokens: z.number().int(),
  cost_usd: z.string(),
});

const lastTurnContextSchema = z.object({
  provider: z.string(),
  model_id: z.string(),
  prompt_tokens: z.number().int(),
  cache_read_tokens: z.number().int(),
  cache_creation_tokens: z.number().int(),
  context_tokens: z.number().int(),
  context_window: z.number().int().nullable(),
  usage_pct: z.number().nullable(),
});

export const sessionUsageSchema = z.object({
  events: z.number().int(),
  prompt_tokens: z.number().int(),
  completion_tokens: z.number().int(),
  total_tokens: z.number().int(),
  cost_usd: z.string(),
  mean_latency_ms: z.number().int().nullable(),
  cache_read_tokens: z.number().int(),
  cache_creation_tokens: z.number().int(),
  by_slot: z.array(slotUsageSchema),
  by_model: z.array(modelUsageSchema),
  last_turn: lastTurnContextSchema.nullable(),
});

export const sessionContextSchema = z.object({
  session_id: z.guid(),
  title: z.string().nullable(),
  started_at: z.string(),
  last_activity_at: z.string(),
  message_count: z.number().int(),
  user_message_count: z.number().int(),
  assistant_message_count: z.number().int(),
  usage: sessionUsageSchema,
});

export type SessionContext = z.infer<typeof sessionContextSchema>;
export type SessionUsage = z.infer<typeof sessionUsageSchema>;
export type SlotUsage = z.infer<typeof slotUsageSchema>;
export type ModelUsage = z.infer<typeof modelUsageSchema>;
export type LastTurnContext = z.infer<typeof lastTurnContextSchema>;

export async function getSessionUsage(sessionId: string): Promise<SessionContext> {
  return apiClient.get(`/chat/sessions/${sessionId}/usage`, sessionContextSchema);
}

// --------------------------------------------------------------------------- //
// User usage status (`add-user-usage-status`)                                  //
// --------------------------------------------------------------------------- //

// The caller's own remaining usage, surfaced in the chat context panel.
// `remaining` is null when the counter store is unavailable; `llm_cost` is
// null unless the cost limit is enabled and a positive per-user limit applies.
// `*_usd` stay strings end-to-end (Decimal serialized server-side).
const rateLimitStatusSchema = z.object({
  scope: z.string(),
  limit: z.number().int(),
  remaining: z.number().int().nullable(),
  window_seconds: z.number().int(),
});

const llmCostBudgetSchema = z.object({
  limit_usd: z.string(),
  spent_usd: z.string(),
  remaining_usd: z.string(),
  // UTC ISO instant the daily window next resets (the budget frees up).
  resets_at: z.string(),
});

export const usageStatusSchema = z.object({
  rate_limits: z.object({ chat_send: rateLimitStatusSchema }),
  llm_cost: llmCostBudgetSchema.nullable(),
});

export type UsageStatus = z.infer<typeof usageStatusSchema>;
export type RateLimitStatus = z.infer<typeof rateLimitStatusSchema>;
export type LlmCostBudget = z.infer<typeof llmCostBudgetSchema>;

export function usageStatusQueryKey(): readonly ["chat", "usage-status"] {
  return ["chat", "usage-status"] as const;
}

export async function getUsageStatus(): Promise<UsageStatus> {
  return apiClient.get("/chat/usage-status", usageStatusSchema);
}

export async function archiveSession(sessionId: string): Promise<void> {
  await apiClient.post(`/chat/sessions/${sessionId}/archive`, VOID, {});
}

export async function unarchiveSession(sessionId: string): Promise<void> {
  await apiClient.post(`/chat/sessions/${sessionId}/unarchive`, VOID, {});
}

// --------------------------------------------------------------------------- //
// Manual title pinning (`add-session-title-autogen`)                           //
// --------------------------------------------------------------------------- //

const updateSessionTitleResponseSchema = z.object({
  id: z.guid(),
  title: z.string(),
  title_is_custom: z.boolean(),
});

export type UpdateSessionTitleResponse = z.infer<typeof updateSessionTitleResponseSchema>;

/** PATCH `/chat/sessions/{id}/title` — manually pin a user-chosen title.
 * Backend sets `title_is_custom=true` so the autogeneration worker never
 * overwrites it on subsequent turns. */
export async function patchSessionTitle(
  sessionId: string,
  title: string,
): Promise<UpdateSessionTitleResponse> {
  return apiClient.patch(`/chat/sessions/${sessionId}/title`, updateSessionTitleResponseSchema, {
    title,
  });
}

// --------------------------------------------------------------------------- //
// Fact-proposal lifecycle clients (`add-chat-fact-proposal-card`)              //
// --------------------------------------------------------------------------- //

const factProposalApproveResponseSchema = z.object({
  draft_id: z.string(),
  status: z.literal("approved"),
  knowledge_item_id: z.guid(),
});

const factProposalCancelResponseSchema = z.object({
  draft_id: z.string(),
  status: z.literal("cancelled"),
});

const factProposalRefineResponseSchema = z.object({
  draft_id: z.string(),
  text: z.string(),
  status: z.literal("pending"),
});

const stageGrantResponseSchema = z.object({
  stage: z.string(),
  status: z.enum(["granted", "already_granted"]),
  confirmation_message: chatMessageReadSchema.nullable().optional().default(null),
});
export type StageGrantResponse = z.infer<typeof stageGrantResponseSchema>;

export async function grantStageAccess(
  sessionId: string,
  stage: "demo" | "dev",
): Promise<StageGrantResponse> {
  return apiClient.post(`/chat/sessions/${sessionId}/stage-grants`, stageGrantResponseSchema, {
    stage,
  });
}

export async function listStageGrants(sessionId: string): Promise<{ stages: string[] }> {
  return apiClient.get(
    `/chat/sessions/${sessionId}/stage-grants`,
    z.object({ stages: z.array(z.string()) }),
  );
}

export async function approveFactProposal(
  messageId: string,
  draftId?: string,
): Promise<z.infer<typeof factProposalApproveResponseSchema>> {
  const body = draftId === undefined ? {} : { draft_id: draftId };
  if (draftId !== undefined) {
    return apiClient.post(
      `/chat/messages/${messageId}/fact-proposal/approve`,
      factProposalApproveResponseSchema,
      body,
    );
  }
  return apiClient.post(
    `/chat/messages/${messageId}/fact-proposal/approve`,
    factProposalApproveResponseSchema,
    {}, // empty body — backend expects no payload
  );
}

export async function cancelFactProposal(
  messageId: string,
  draftId?: string,
): Promise<z.infer<typeof factProposalCancelResponseSchema>> {
  const body = draftId === undefined ? {} : { draft_id: draftId };
  return apiClient.post(
    `/chat/messages/${messageId}/fact-proposal/cancel`,
    factProposalCancelResponseSchema,
    body,
  );
}

export async function refineFactProposal(
  messageId: string,
  instructions: string,
  draftId?: string,
): Promise<z.infer<typeof factProposalRefineResponseSchema>> {
  const body = draftId === undefined ? { instructions } : { instructions, draft_id: draftId };
  return apiClient.post(
    `/chat/messages/${messageId}/fact-proposal/refine`,
    factProposalRefineResponseSchema,
    body,
  );
}

// --- user-memory draft direct actions (add-direct-memory-draft-actions) ----- #

const userMemoryDraftDecisionSchema = z.object({
  draft_id: z.string(),
  status: z.enum(["approved", "cancelled"]),
  memory_item_id: z.guid().nullable().optional(),
});

/** Apply a pending memory draft directly (a confirm click). `text` overrides
 * the proposed text — a manual inline edit. */
export async function approveUserMemoryDraft(
  messageId: string,
  draftId: string,
  text?: string,
): Promise<z.infer<typeof userMemoryDraftDecisionSchema>> {
  const body = text === undefined ? { draft_id: draftId } : { draft_id: draftId, text };
  return apiClient.post(
    `/chat/messages/${encodeURIComponent(messageId)}/user-memory-draft/approve`,
    userMemoryDraftDecisionSchema,
    body,
  );
}

/** Discard a pending memory draft directly (a cancel click). */
export async function cancelUserMemoryDraft(
  messageId: string,
  draftId: string,
): Promise<z.infer<typeof userMemoryDraftDecisionSchema>> {
  return apiClient.post(
    `/chat/messages/${encodeURIComponent(messageId)}/user-memory-draft/cancel`,
    userMemoryDraftDecisionSchema,
    { draft_id: draftId },
  );
}

/** Wire shape (snake_case, `cost_usd` as a decimal string or null)
 * emitted by the backend on `step` events and persisted in
 * `metadata.steps[*].usage`. Parsed into `StepUsage` at the edge. */
export interface StepUsageWire {
  /** model_settings slot the model was resolved from. Optional only for
   * back-compat: production always sets it; rows persisted before slot
   * was tracked omit it. `| undefined` so the persisted Zod shape
   * (`z.string().optional()`) is assignable under exactOptionalPropertyTypes. */
  slot?: string | undefined;
  model: string;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  cost_usd: string | null;
  cost_source: "computed" | "unknown";
  /** Input tokens served from / written to the provider's prompt cache,
   * already summed across the step's tool-loop rounds (default 0). The
   * `answer` step carries these so the meta strip can sum the cached
   * figure the same way it sums in/out/cost. `| undefined` so the
   * persisted Zod shape (`z.number().optional()`) is assignable under
   * exactOptionalPropertyTypes — mirrors `slot` above. */
  cache_read_input_tokens?: number | undefined;
  cache_creation_input_tokens?: number | undefined;
  /** True when a per-slot provider failover fallback leg (wrapper leg or
   * credit-outage bypass) actually served this step's LLM call. Carried on
   * the `answer` step so the live meta strip can attribute the served model
   * and render its badge at stream completion. Key absent on primary-served
   * turns and historical rows (fix-served-model-attribution-on-failover). */
  fallback_used?: boolean | undefined;
  /** Every provider call of the step, in order; present only when a decision
   * engine took part. Absent on historical rows. */
  calls?: StepCallWire[] | undefined;
}

/** The value a provider call decided, by question id (`level`, `intent`
 * strings; `needs_retrieval`, `needs_graph` booleans). */
export type StepCallAnswer = Record<string, string | boolean>;

/** Wire shape of one provider call inside a step. `label`, `outcome`,
 * `reason`, `confidences` and `answer` are absent when unset; `latency_ms`
 * and `cost_usd` may be null. `| undefined` for exactOptionalPropertyTypes. */
export interface StepCallWire {
  kind: "decision_engine" | "llm";
  provider: string;
  label?: string | undefined;
  model: string;
  latency_ms?: number | null | undefined;
  prompt_tokens: number;
  completion_tokens: number;
  cost_usd: string | null;
  outcome?: "served" | "fell_through" | "failed" | undefined;
  reason?: string | undefined;
  confidences?: Record<string, number> | undefined;
  answer?: StepCallAnswer | undefined;
}

/** In-memory camelCase provider call of a step (decision engine or LLM).
 * `costUsd` is a number, or null when pricing is unknown. */
export interface StepCall {
  kind: "decision_engine" | "llm";
  provider: string;
  label?: string;
  model: string;
  latencyMs: number | null;
  promptTokens: number;
  completionTokens: number;
  costUsd: number | null;
  outcome?: "served" | "fell_through" | "failed";
  reason?: string;
  confidences?: Record<string, number>;
  /** What the call decided, by question id; absent when it parsed no
   * answer (e.g. a failed engine call). */
  answer?: StepCallAnswer;
}

/** In-memory camelCase per-step LLM usage. `costUsd` is a number (or
 * null when pricing is unknown); the backend ships it as a decimal
 * string to preserve precision, parsed here once at the edge. */
export interface StepUsage {
  /** model_settings slot the model was resolved from (e.g. `utility`).
   * Optional for back-compat with rows persisted before slot tracking. */
  slot?: string;
  model: string;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  costUsd: number | null;
  costSource: "computed" | "unknown";
  /** Prompt-cache input tokens, summed across the step's rounds (default 0).
   * `cacheRead` is the meaningful figure surfaced on the meta strip. */
  cacheRead: number;
  cacheCreation: number;
  /** True when a failover fallback leg served this step's LLM call (see
   * `StepUsageWire.fallback_used`). Only set when true — absent otherwise. */
  fallbackUsed?: boolean;
  /** Every provider call of the step, in order. Only set when the wire
   * carries it (a decision engine took part). */
  calls?: StepCall[];
}

function parseStepCall(wire: StepCallWire): StepCall {
  return {
    kind: wire.kind,
    provider: wire.provider,
    ...(wire.label !== undefined ? { label: wire.label } : {}),
    model: wire.model,
    latencyMs: wire.latency_ms ?? null,
    promptTokens: wire.prompt_tokens,
    completionTokens: wire.completion_tokens,
    costUsd: wire.cost_usd !== null ? Number(wire.cost_usd) : null,
    ...(wire.outcome !== undefined ? { outcome: wire.outcome } : {}),
    ...(wire.reason !== undefined ? { reason: wire.reason } : {}),
    ...(wire.confidences !== undefined ? { confidences: wire.confidences } : {}),
    ...(wire.answer !== undefined ? { answer: wire.answer } : {}),
  };
}

/** Parse the wire usage shape into the in-memory `StepUsage`. */
export function parseStepUsage(wire: StepUsageWire): StepUsage {
  return {
    ...(wire.slot !== undefined ? { slot: wire.slot } : {}),
    model: wire.model,
    promptTokens: wire.prompt_tokens,
    completionTokens: wire.completion_tokens,
    totalTokens: wire.total_tokens,
    costUsd: wire.cost_usd !== null ? Number(wire.cost_usd) : null,
    costSource: wire.cost_source,
    cacheRead: wire.cache_read_input_tokens ?? 0,
    cacheCreation: wire.cache_creation_input_tokens ?? 0,
    ...(wire.fallback_used ? { fallbackUsed: true } : {}),
    ...(wire.calls !== undefined ? { calls: wire.calls.map(parseStepCall) } : {}),
  };
}

export interface StepState {
  id: string;
  status: "running" | "done" | "error";
  startedAt: number;
  elapsedMs?: number;
  detail?: {
    hits?: number;
    bytes?: number;
    stage?: string;
    fused?: number;
    error?: string;
    decision?: string;
    reason?: string;
    tier?: string;
    selected?: {
      provider: string;
      model_id: string;
      display_name?: string;
    };
    shadow?: boolean;
    sticky?: boolean;
    would_skip?: boolean;
    skipped?: boolean;
    intent?: string;
    decided_by?: string;
  };
  usage?: StepUsage;
}

/** Aggregate per-step usage into a pipeline total. Sums prompt and
 * completion tokens (and prompt-cache reads) across every step that carries
 * usage; `costUsd` sums the known costs (unknown/null treated as zero).
 * `cacheRead` sums the per-step cache reads (each already summed across that
 * step's tool-loop rounds), so the figure no longer under-reports on
 * multi-round turns the way the per-turn `usage` event did. Returns `null`
 * when no step has usage, so callers can omit the total entirely. */
export function sumStepsUsage(
  steps: StepState[],
): { promptTokens: number; completionTokens: number; costUsd: number; cacheRead: number } | null {
  const withUsage = steps.filter((s) => s.usage !== undefined);
  if (withUsage.length === 0) return null;
  let promptTokens = 0;
  let completionTokens = 0;
  let costUsd = 0;
  let cacheRead = 0;
  for (const s of withUsage) {
    const u = s.usage as StepUsage;
    promptTokens += u.promptTokens;
    completionTokens += u.completionTokens;
    cacheRead += u.cacheRead;
    if (u.costUsd !== null) costUsd += u.costUsd;
  }
  return { promptTokens, completionTokens, costUsd, cacheRead };
}

/** In-memory shape of a single tool dispatch in an assistant turn.
 * Populated by the SSE reducer (`tool_use` + `tool_result` events) and
 * the metadata-hydration path. Lives here (not in the chat route) so
 * `PipelineSteps` can fold the tool-call body under its corresponding
 * `tool.*` step row without importing from the route. */
export interface ToolCall {
  id: string;
  name: string;
  input: Record<string, unknown>;
  ok?: boolean;
  bytes?: number;
  content_preview?: string;
  error?: string;
  ref?: string;
  stage?: string;
  ref_fallback?: boolean;
  tried_refs?: string[];
}

/** Pre-computed display strings for a `ToolCall`. The `target` /
 * `refText` columns are the only tool-specific fields the row body
 * surfaces; everything else (status, icon, label, size, elapsed) is
 * already on the step row itself. */
export interface ToolDisplay {
  icon: string;
  label: string;
  target: string;
  sizeText: string | null;
  refText: string | null;
}

/** Tool-specific extraction of human-friendly target / refText
 * strings from the raw tool input. Shared between the inline
 * `ToolCallBlock` (used for `render_diagram`) and the step-tree row
 * expansion. */
function githubRefText(call: ToolCall): string | null {
  if (!call.ref) return null;
  const label = call.ref.length > 12 ? call.ref.slice(0, 7) : call.ref;
  if (call.ref_fallback) return `${label} (fallback)`;
  return label;
}

/** Count line the deploy-history tool emits as its preview: `"<N> merges into <base_ref>"`. */
const RECENT_MERGES_PREVIEW = /^(\d+) merges? into /;

/** Base branch, fallback flag and merge count of a `github_list_recent_merges` call. */
function recentMergesRefText(call: ToolCall): string | null {
  const countMatch =
    call.ok === false || call.content_preview === undefined
      ? null
      : RECENT_MERGES_PREVIEW.exec(call.content_preview);
  const count = countMatch?.[1] === undefined ? null : Number(countMatch[1]);
  const parts = [
    call.ref ? (call.ref_fallback ? `${call.ref} (fallback)` : call.ref) : null,
    count === null ? null : `${count} ${count === 1 ? "merge" : "merges"}`,
  ].filter((part): part is string => part !== null);
  return parts.length ? parts.join(" · ") : null;
}

export function describeTool(call: ToolCall): ToolDisplay {
  if (call.name === "github_list_recent_merges") {
    return {
      icon: "🔀",
      label: "recent_merges",
      target: typeof call.input.repo === "string" ? call.input.repo : "",
      sizeText: null,
      refText: recentMergesRefText(call),
    };
  }
  if (call.name === "github_read_file" || call.name === "github_list_dir") {
    const repo = typeof call.input.repo === "string" ? call.input.repo : "";
    const path = typeof call.input.path === "string" ? call.input.path : "";
    return {
      icon: call.name === "github_read_file" ? "📄" : "📂",
      label: call.name === "github_read_file" ? "read_file" : "list_dir",
      target: `${repo}${path ? "/" : ""}${path}`,
      sizeText:
        call.name === "github_list_dir" && call.bytes !== undefined
          ? `${call.bytes} entries`
          : call.bytes
            ? `${(call.bytes / 1024).toFixed(1)} KB`
            : null,
      refText: githubRefText(call),
    };
  }
  if (call.name === "query_cloudwatch_logs") {
    const logGroup = typeof call.input.log_group === "string" ? call.input.log_group : "";
    const start = typeof call.input.start_time === "string" ? call.input.start_time : "";
    const end = typeof call.input.end_time === "string" ? call.input.end_time : "";
    const range = start && end ? `${start.slice(0, 16)} → ${end.slice(0, 16)}` : null;
    return {
      icon: "☁️",
      label: "cloudwatch",
      target: logGroup,
      sizeText: call.bytes ? `${(call.bytes / 1024).toFixed(1)} KB` : null,
      refText: range,
    };
  }
  if (call.name === "query_mongodb") {
    const collection = typeof call.input.collection === "string" ? call.input.collection : "";
    const pipeline = Array.isArray(call.input.pipeline) ? call.input.pipeline : [];
    return {
      icon: "🍃",
      label: "mongo",
      target: collection,
      sizeText: call.bytes ? `${(call.bytes / 1024).toFixed(1)} KB` : null,
      refText: pipeline.length ? `${pipeline.length} stages` : null,
    };
  }
  if (call.name === "slack_search") {
    const queryChars =
      typeof call.input.query_chars === "number" && Number.isFinite(call.input.query_chars)
        ? Math.max(0, Math.floor(call.input.query_chars))
        : null;
    const channels = Array.isArray(call.input.channels)
      ? call.input.channels.filter((value): value is string => typeof value === "string")
      : [];
    const limit =
      typeof call.input.limit === "number" && Number.isInteger(call.input.limit)
        ? call.input.limit
        : null;
    const after = typeof call.input.after === "string" ? call.input.after : null;
    const before = typeof call.input.before === "string" ? call.input.before : null;
    const shape = [
      queryChars === null ? null : `${queryChars} caracteres`,
      limit === null ? null : `limit ${limit}`,
      after ? `desde ${formatDate(after.slice(0, 10))}` : null,
      before ? `hasta ${formatDate(before.slice(0, 10))}` : null,
    ].filter((value): value is string => value !== null);
    return {
      icon: "💬",
      label: "buscando en Slack",
      target: channels.length
        ? channels.map((channel) => `#${channel}`).join(", ")
        : "canales autorizados",
      sizeText: call.bytes ? `${(call.bytes / 1024).toFixed(1)} KB` : null,
      refText: shape.length ? shape.join(" · ") : null,
    };
  }
  if (call.name === "render_diagram") {
    // `call.input` is `Record<string, unknown>`; narrow each field via
    // typeof guards before assignment (no `as` / `any` per CLAUDE.md #2).
    const title = typeof call.input.title === "string" ? call.input.title : "";
    const diagramType = typeof call.input.diagram_type === "string" ? call.input.diagram_type : "";
    return {
      icon: "📊",
      label: "diagram",
      target: title,
      sizeText: null,
      refText: diagramType || null,
    };
  }
  return {
    icon: "🔧",
    label: call.name,
    target: "",
    sizeText: call.bytes !== undefined ? `${call.bytes}` : null,
    refText: null,
  };
}

/**
 * Derive a step's visual parent from its id.
 * - `retrieval.*` → `"retrieval"`
 * - `tool.*` → `"tool"` (virtual parent; UI renders tool steps indented under `answer`)
 * - `answer.continuation` and other answer phases → `null` (sequential phases,
 *   not parallel children of `answer`)
 * - everything else → `null` (top-level)
 */
export function parentOf(id: string): string | null {
  if (id.startsWith("retrieval.")) return "retrieval";
  if (id.startsWith("tool.")) return "tool";
  return null;
}

/** Best-effort extraction of a raw `type` value from an already-parsed SSE
 * block, for the warning logged on a validation failure below. Returns
 * `"<unknown>"` when `parsed` isn't a plain object or has no `type` key —
 * this is purely diagnostic, so it never throws. */
function rawEventType(parsed: unknown): string {
  if (typeof parsed !== "object" || parsed === null || !("type" in parsed)) {
    return "<unknown>";
  }
  const value = (parsed as { type: unknown }).type;
  return typeof value === "string" ? value : "<unknown>";
}

export function parseSseBlock(block: string): ChatStreamEvent | null {
  const lines = block.split("\n");
  const data = lines
    .filter((l) => l.startsWith("data:"))
    .map((l) => l.slice("data:".length).trim())
    .join("\n");
  if (!data) return null;
  try {
    const parsed: unknown = JSON.parse(data);
    const result = chatSseEventSchema.safeParse(parsed);
    if (!result.success) {
      // Dropped, not thrown: an unrecognized `type` (e.g. a newer backend
      // deploy adding an event type this bundle doesn't know about yet) or a
      // payload missing a field its declared `type` requires must not abort
      // the SSE read loop for subsequent events (openspec
      // `type-chat-sse-events`, chat/spec.md's two "dropped without breaking
      // the stream" scenarios).
      console.warn("[chat] dropping invalid SSE event", {
        type: rawEventType(parsed),
        issues: result.error.issues,
      });
      return null;
    }
    return result.data as ChatStreamEvent;
  } catch {
    return null;
  }
}
